import React, { useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import { ColumnDefinition } from '@amzn/dots-core-ui';
import { useHistory } from 'react-router-dom';
import { CoreSearchTable } from '../../../components';
import { Build, Project, useClient } from '../../../api';
import { BuildFileUri } from '../../routes';
import { BuildVersionFilters } from './BuildVersionFilters';

// List out filters for the search table (coresearchtable)
const filters = [
  { key: 'version', label: 'Version', isDefault: true },
  { key: 'status', label: 'Status' },
  { key: 'buildtype', label: 'Build Type' },
];

export const buildVersionColumnDefinitionsFactory = (
  onBuildClick: (build: Build) => void
): ColumnDefinition<Build>[] => [
  {
    id: 'version',
    header: 'Version',
    item: (version): JSX.Element => (
      <Button onClick={() => onBuildClick(version)}>{version.version}</Button>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    item: (version): string => version.status,
  },
  {
    id: 'buildtype',
    header: 'Build Type',
    item: (version): string => version.buildtype,
  },
  {
    id: 'created',
    header: 'Created',
    item: (version): string =>
      version.created instanceof Date
        ? version.created.toLocaleDateString()
        : new Date(version.created).toLocaleDateString(),
  },
  {
    id: 'gm',
    header: 'GM',
    item: (version): string => (version.gm ? 'Yes' : 'No'),
  },
];

export type buildVersionListProps = {
  project: Project;
};

// Custom getAll function for the CoreSearchTable
const createGetAllFunction = (
  project: Project,
  getVersions: any,
  additionalFilters: Map<string, string>
) => {
  return async (params: any) => {
    const { version, status, buildtype, fuzzySearch } = params;

    // Get all versions for the project
    const response = await getVersions(project.name);

    // Filter the versions based on the search parameters
    let filteredContent = [...response.content];

    // Helper function for fuzzy search
    const fuzzyMatch = (text: string, query: string): boolean => {
      if (!fuzzySearch) {
        return text.toLowerCase().includes(query.toLowerCase());
      }

      // Simple fuzzy search implementation
      const pattern = query.toLowerCase().split('').join('.*');
      const regex = new RegExp(pattern);
      return regex.test(text.toLowerCase());
    };

    // Apply search filters
    if (version) {
      filteredContent = filteredContent.filter((build) =>
        fuzzyMatch(build.version, version)
      );
    }

    if (status) {
      filteredContent = filteredContent.filter((build) =>
        fuzzyMatch(build.status, status)
      );
    }

    if (buildtype) {
      filteredContent = filteredContent.filter((build) =>
        fuzzyMatch(build.buildtype, buildtype)
      );
    }

    additionalFilters.forEach((value, key) => {
      if (value) {
        filteredContent = filteredContent.filter((build) => {
          // Apply filter based on key
          switch (key) {
            case 'build version':
              return fuzzyMatch(build.version, value);
            case 'status':
              return fuzzyMatch(build.status, value);
            case 'build type':
              return fuzzyMatch(build.buildtype, value);
            default:
              // For other filters, check if the build has the property
              return build[key] ? fuzzyMatch(build[key], value) : false;
          }
        });
      }
    });

    // Return the filtered response
    return {
      ...response,
      content: filteredContent,
      total: filteredContent.length,
      count: filteredContent.length,
    };
  };
};

export const BuildVersionList = ({
  project,
}: buildVersionListProps): JSX.Element => {
  const history = useHistory();
  const { projects } = useClient();
  const [additionalFilters, setAdditionalFilters] = useState<
    Map<string, string>
  >(new Map());

  const handleBuildClick = (build: Build) => {
    history.push(`${BuildFileUri}/${build.project}/${build.version}`);
  };

  const handleFilterChange = (filter: string, value: string | undefined) => {
    const newFilters = new Map(additionalFilters);
    if (value) {
      newFilters.set(filter, value);
    } else {
      newFilters.delete(filter);
    }
    setAdditionalFilters(newFilters);
  };

  // Create a custom getAll function for the CoreSearchTable
  const getAllVersions = useMemo(
    () =>
      createGetAllFunction(project, projects.getVersions, additionalFilters),
    [project, projects.getVersions, additionalFilters]
  );

  const columnDefinitions = useMemo(
    () => buildVersionColumnDefinitionsFactory(handleBuildClick),
    [handleBuildClick]
  );

  return (
    <Column spacingInset="400">
      <Row>
        <Heading level={3}>Build Versions</Heading>
      </Row>

      <BuildVersionFilters
        projectName={project.name}
        filters={additionalFilters}
        onFilterChange={handleFilterChange}
      />

      <CoreSearchTable
        resourceKey={`build-versions-${project.id}`}
        getAll={getAllVersions}
        resourceFilters={filters}
        columns={columnDefinitions}
        showSearch={false}
      />
    </Column>
  );
};
